import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import { IPlace } from '@okcargo-apps/shared-domain-ionic';
import { CountryStateService } from '@okcargo-apps/shared-infrastructure-ionic';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { PlaceService } from '../place.service';

@Component({
  selector: 'lib-search-city-postalcode-modal',
  templateUrl: './search-city-postalcode-modal.component.html',
  styleUrls: ['./search-city-postalcode-modal.component.scss'],
  imports: [
    IonLabel,
    IonContent,
    IonTitle,
    IonIcon,
    IonButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    CommonModule,
    ReactiveFormsModule,
    IonCol,
    IonRow,
    IonGrid,
    IonItem,
    IonInput,
    IonSelect,
    IonSelectOption,
    SpinnerLoadingComponent,
  ],
})
export class SearchCityPostalcodeModalComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly placesService = inject(PlaceService);
  private readonly countryStateService = inject(CountryStateService);
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);

  private readonly selectedCountrySubject = new BehaviorSubject<string>('ES');
  private readonly searchTermSubject = new BehaviorSubject<string>('');
  private readonly destroySubject = new Subject<void>();

  public readonly countries$ = this.countryStateService.countries$.pipe(filter((countries) => countries.length > 0));

  @ViewChild('postalCodeInput')
  postalCodeInput!: IonInput;

  public readonly formAddressEdit = this.fb.group({
    country: ['ES', Validators.required],
    postalCode: ['', Validators.minLength(2)],
  });

  public readonly places$ = combineLatest([
    this.searchTermSubject.pipe(debounceTime(300), distinctUntilChanged()),
    this.selectedCountrySubject,
  ]).pipe(
    switchMap(([term, country]) =>
      term.trim().length < 2
        ? of([])
        : this.placesService.searchPlaces$(term, country).pipe(
            map((results) => results.slice(0, 20)),
            catchError((err) => {
              console.error('Error al buscar lugares:', err);
              return of([]);
            }),
          ),
    ),
    takeUntil(this.destroySubject),
  );

  ngOnInit(): void {
    this.countries$.pipe(take(1), takeUntil(this.destroySubject)).subscribe((countries) => {
      const defaultCountry = countries.find((c) => c.isoCode === 'ES')?.isoCode || 'ES';
      this.formAddressEdit.patchValue({ country: defaultCountry });
      this.selectedCountrySubject.next(defaultCountry);
    });

    this.formAddressEdit
      .get('country')
      ?.valueChanges.pipe(
        takeUntil(this.destroySubject),
        filter((value): value is string => value !== null && value !== undefined),
      )
      .subscribe((value) => {
        this.selectedCountrySubject.next(value);
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.postalCodeInput) {
        this.postalCodeInput.setFocus();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  get loading(): boolean {
    return this.placesService.searchPlacesLoading();
  }

  public handleChangeTerm(event: any): void {
    this.searchTermSubject.next(event.target.value);
  }

  public async handleSelectedPostalCode(place: IPlace): Promise<boolean> {
    const country$ = this.countryStateService.findCountryByCode$(place.countryCode).pipe(
      catchError((error) => {
        // TODO show error message?
        return of(null);
      }),
    );

    const state$ = this.countryStateService.findStateByCode$(place.countryCode, place.stateCode).pipe(
      catchError((error) => {
        // TODO show error message?
        return of(null);
      }),
    );

    const updatedPlace$ = forkJoin([country$, state$]).pipe(
      map(([country, state]) => ({
        ...place,
        country,
        state: state?.state,
      })),
      switchMap((updatedPlace) => {
        const dismissResult = this.modalCtrl.dismiss(updatedPlace, 'confirm');
        return of(dismissResult);
      }),
      catchError((error) => {
        // TODO show error message?
        return of(false);
      }),
    );

    return firstValueFrom(updatedPlace$);
  }

  public async closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
